import { useEffect, useState } from 'react';

import ClaimTheme from '@/james-playground/aa-family-claim/ClaimTheme';
import BeforeScreen, { beforeAsks } from '@/james-playground/aa-family-claim/BeforeScreen';
import ClaimHomeScreen from '@/james-playground/aa-family-claim/ClaimHomeScreen';
import HandoffScreen from '@/james-playground/aa-family-claim/HandoffScreen';
import HubScreen from '@/james-playground/aa-family-claim/HubScreen';
import ReviewScreen from '@/james-playground/aa-family-claim/ReviewScreen';
import RoundScreen, { type Recording } from '@/james-playground/aa-family-claim/RoundScreen';
import SendScreen from '@/james-playground/aa-family-claim/SendScreen';
import SentScreen from '@/james-playground/aa-family-claim/SentScreen';
import WelcomeScreen from '@/james-playground/aa-family-claim/WelcomeScreen';
import { startClaim, voiceOf, type ClaimState } from '@/james-playground/aa-family-claim/claim/claim';
import { openFor, progress, skipped, toCheck, type Handoff, type Step } from '@/james-playground/aa-family-claim/claim/engine';
import { HUB } from '@/james-playground/aa-family-claim/claim/claim.content';
import { text } from '@/james-playground/aa-family-claim/claim/facts';
import { SCENARIO, type ScenarioId } from '@/james-playground/aa-family-claim/claim/scenarios';
import { ENTRIES, isAsked } from '@/james-playground/aa-family-claim/claim/registry';
import { fillOfficialAaPdf } from '@/james-playground/aa-family-claim/pdf/fillOfficialAaPdf';

export type Frame =
  | 'welcome'
  | 'before'
  | 'handoff'
  | 'hub'
  | 'round'
  | 'check'
  | 'send'
  | 'sent'
  | 'home';

/** What a Loft scene or story opens the claim with. */
export type Setup = {
  frame: Frame;
  scenario?: ScenarioId;
  /** Puts the claim in place before the screen opens: answers, rounds run. */
  prepare?: (claim: ClaimState) => ClaimState;
  /** For a round: which step it runs. */
  step?: (claim: ClaimState) => Step;
  handoff?: Handoff;
  recording?: Recording;
};

export const buildClaim = (setup: Pick<Setup, 'scenario' | 'prepare'>): ClaimState => {
  const claim = startClaim(setup.scenario ?? 'standard');
  return setup.prepare ? setup.prepare(claim) : claim;
};

/** Answers the claim holds, for the Sent and Home screens. */
export const answeredCount = (claim: ClaimState) =>
  ENTRIES.filter((e) => isAsked(e) && claim.facts[e.id]?.state === 'confirmed').length;

const AaFamilyClaim = ({
  initial = { frame: 'welcome' },
  today = new Date(),
  onFrameChange,
  onClaimChange,
}: {
  initial?: Setup;
  today?: Date;
  onFrameChange?: (frame: Frame) => void;
  /** The stage listens, so the adviser side can show the claim just sent. */
  onClaimChange?: (claim: ClaimState) => void;
}) => {
  const [frame, setFrame] = useState<Frame>(initial.frame);
  const [claim, setClaimState] = useState<ClaimState>(() => buildClaim(initial));
  const [step, setStep] = useState<Step | undefined>(() =>
    initial.frame === 'round' && initial.step ? initial.step(buildClaim(initial)) : undefined,
  );
  const [handoff, setHandoff] = useState<Handoff>(initial.handoff ?? 'scotland');
  const [resumeTo, setResumeTo] = useState<Frame>('hub');
  const v = voiceOf(claim);

  const setClaim = (next: ClaimState) => {
    setClaimState(next);
    onClaimChange?.(next);
  };
  const go = (next: Frame) => {
    setFrame(next);
    onFrameChange?.(next);
  };
  const leave = (from: Frame) => () => {
    setResumeTo(from === 'round' ? 'hub' : from);
    go('home');
  };

  useEffect(() => {
    onFrameChange?.(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const download = async () => {
    try {
      const bytes = await fillOfficialAaPdf(claim.facts);
      const copied = new Uint8Array(bytes.length);
      copied.set(bytes);
      const url = URL.createObjectURL(new Blob([copied], { type: 'application/pdf' }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${SCENARIO[claim.scenario].claimant.full.replace(/[^a-zA-Z0-9]+/g, '-')}-AA1-draft.pdf`;
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (cause) {
      console.error(cause);
    }
  };

  const t = (line: string) => line.replace(/Margaret/g, v.name);

  const screen = (() => {
    switch (frame) {
      case 'welcome':
        return <WelcomeScreen voice={v} onStart={() => go(beforeAsks(claim).length ? 'before' : 'hub')} />;
      case 'before':
        return (
          <BeforeScreen
            claim={claim}
            today={today}
            onChange={setClaim}
            onRoute={(route) => {
              setHandoff(route);
              go('handoff');
            }}
            onDone={(next) => {
              setClaim(next);
              go('hub');
            }}
            onLeave={leave('before')}
          />
        );
      case 'handoff':
        return <HandoffScreen handoff={handoff} voice={v} onBack={() => go('before')} />;
      case 'hub':
        return (
          <HubScreen
            claim={claim}
            today={today}
            onStep={(next) => {
              setStep(next);
              go('round');
            }}
            onCheck={() => go(toCheck(claim.facts).length ? 'check' : 'send')}
            onLeave={leave('hub')}
            onSeenArrivals={() => setClaim({ ...claim, arrivals: [] })}
          />
        );
      case 'round':
        return step ? (
          <RoundScreen
            key={`${step.kind}-${claim.log.length}`}
            claim={claim}
            step={step}
            initialRecording={initial.frame === 'round' ? initial.recording : undefined}
            onBack={() => go('hub')}
            onDone={(next) => {
              setClaim(next);
              go('hub');
            }}
          />
        ) : null;
      case 'check':
        return (
          <ReviewScreen
            claim={claim}
            onChange={setClaim}
            onBack={() => go('hub')}
            onNext={() => go(openFor(claim.facts, 1).length ? 'hub' : 'send')}
          />
        );
      case 'send':
        return (
          <SendScreen
            claim={claim}
            onBack={() => go('hub')}
            onDownload={() => void download()}
            onSend={() => go('sent')}
          />
        );
      case 'sent':
        return (
          <SentScreen
            t={t}
            answers={answeredCount(claim)}
            left={skipped(claim.facts).length}
            sources={[
              claim.rounds.openingDone && claim.aiConsent === 'yes' ? 'Your voice note' : '',
              ...claim.rounds.documentsDone.map((d) => (d === 'prescription' ? 'the prescription' : d === 'care_plan' ? 'the care plan' : 'the letter')),
            ]
              .filter(Boolean)
              .join(' and ')}
            onHome={() => {
              setClaim(startClaim(claim.scenario));
              go('welcome');
            }}
            onDownload={() => void download()}
          />
        );
      case 'home':
        return (
          <ClaimHomeScreen
            t={t}
            progress={HUB.headline[progress(claim.facts)]}
            initials={(SCENARIO[claim.scenario].helper ?? SCENARIO[claim.scenario].claimant).full
              .split(' ')
              .map((w) => w[0])
              .join('')}
            onContinue={() => go(resumeTo)}
          />
        );
    }
  })();

  return <ClaimTheme key={claim.scenario}>{screen}</ClaimTheme>;
};

/** Whether the claim is on the quicker route, for the stage's flags. */
export const isQuicker = (claim: ClaimState) => text(claim.facts, 'specialRules') === 'Yes';

export default AaFamilyClaim;
